import { printPayslip } from '../utils/print';
import { getBrandingSlug } from '../utils/formatters';
import { KpiCard, AreaChart, BarChartV, DonutRing, RankBars, DashCard, ActivityFeed, Gauge } from '../components/DashboardWidgets';
import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader, StatCards, Card, TabBar, Field, inputStyle, btnPrimary, btnSm, Empty, fmtKES } from '../components/UI';

// ─── Kenya Statutory Constants (2024/2025) ───────────────────────────────────
const PAYE_BANDS = [
  { min: 0,      max: 24000,  rate: 0.10 },
  { min: 24001,  max: 32333,  rate: 0.25 },
  { min: 32334,  max: 500000, rate: 0.30 },
  { min: 500001, max: 800000, rate: 0.325},
  { min: 800001, max: Infinity,rate:0.35  },
];
const PERSONAL_RELIEF = 2400;
const NSSF_TIER1_MAX  = 7000;
const NSSF_TIER2_MAX  = 36000;
const NSSF_TIER1_RATE = 0.06;
const NSSF_TIER2_RATE = 0.06;
const NHIF_TABLE = [
  [0, 5999, 150], [6000, 7999, 300], [8000, 11999, 400], [12000, 14999, 500],
  [15000, 19999, 600], [20000, 24999, 750], [25000, 29999, 850], [30000, 34999, 900],
  [35000, 39999, 950], [40000, 44999, 1000], [45000, 49999, 1100], [50000, 59999, 1200],
  [60000, 69999, 1300], [70000, 79999, 1400], [80000, 89999, 1500], [90000, 99999, 1600],
  [100000, Infinity, 1700],
];
const HOUSING_LEVY_RATE = 0.015; // 1.5% employer, 1.5% employee

// ─── Computations ─────────────────────────────────────────────────────────────
function computeNSSF(gross) {
  const tier1 = Math.min(gross, NSSF_TIER1_MAX) * NSSF_TIER1_RATE;
  const tier2 = Math.max(0, Math.min(gross, NSSF_TIER2_MAX) - NSSF_TIER1_MAX) * NSSF_TIER2_RATE;
  return Math.round(tier1 + tier2);
}
function computeNHIF(gross) {
  const row = NHIF_TABLE.find(r => gross >= r[0] && gross <= r[1]);
  return row ? row[2] : 1700;
}
function computePAYE(taxableIncome) {
  let tax = 0;
  for (const band of PAYE_BANDS) {
    if (taxableIncome <= 0) break;
    const bandAmt = Math.min(taxableIncome, band.max - band.min + (band.min === 0 ? 1 : 0));
    tax += Math.max(0, bandAmt) * band.rate;
    taxableIncome -= bandAmt;
  }
  return Math.max(0, Math.round(tax - PERSONAL_RELIEF));
}
function computePayroll(emp, period = {}) {
  const basic      = parseFloat(emp.basic_salary) || 0;
  const house      = parseFloat(emp.house_allowance) || 0;
  const transport  = parseFloat(emp.transport_allowance) || 0;
  const medical    = parseFloat(emp.medical_allowance) || 0;
  const otherAllow = parseFloat(emp.other_allowance) || 0;
  const overtime   = parseFloat(period.overtime_pay) || 0;
  const bonus      = parseFloat(period.bonus) || 0;
  const grossPay   = basic + house + transport + medical + otherAllow + overtime + bonus;

  const nssf       = computeNSSF(basic);
  const nhif       = computeNHIF(grossPay);
  const housingLevy= Math.round(grossPay * HOUSING_LEVY_RATE);
  const taxable    = grossPay - nssf;
  const paye       = computePAYE(taxable);
  const loanDeduct = parseFloat(period.loan_deduction) || 0;
  const advanceDeduct = parseFloat(period.advance_deduction) || 0;
  const totalDeductions = nssf + nhif + housingLevy + paye + loanDeduct + advanceDeduct;
  const netPay     = grossPay - totalDeductions;

  return { basic, house, transport, medical, otherAllow, overtime, bonus, grossPay, nssf, nhif, housingLevy, paye, taxable, loanDeduct, advanceDeduct, totalDeductions, netPay };
}

const blankEmp = {
  name: '', emp_id: '', department: '', job_title: '', id_number: '', kra_pin: '',
  nssf_number: '', nhif_number: '', phone: '', email: '',
  bank_name: '', bank_account: '', bank_branch: '',
  basic_salary: '', house_allowance: '', transport_allowance: '', medical_allowance: '', other_allowance: '',
  employment_type: 'permanent', start_date: '', status: 'active',
  loan_balance: '', advance_balance: '',
};

const DEPTS = ['Management', 'Front Office', 'Food & Beverage', 'Housekeeping', 'Security', 'Maintenance', 'Spa & Wellness', 'Accounts', 'Marketing', 'Kitchen', 'Gift Shop', 'Pool & Recreation'];

export default function PayrollPage({ notify, userRole }) {
  const [tab, setTab] = useState('dashboard');
  const [employees, setEmployees] = useState([]);
  const [payrollRuns, setPayrollRuns] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [showEmpForm, setShowEmpForm] = useState(false);
  const [editEmp, setEditEmp] = useState(null);
  const [empForm, setEmpForm] = useState(blankEmp);
  const [selectedEmp, setSelectedEmp] = useState(null);

  // Period overrides per employee
  const [periodData, setPeriodData] = useState({}); // { empId: { overtime_pay, bonus, loan_deduction, advance_deduction } }
  const [locked, setLocked] = useState(false);
  const [processing, setProcessing] = useState(false);

  const canHR = ['admin', 'hr'].includes(userRole) || !userRole;
  const canFinance = ['admin', 'accountant', 'finance'].includes(userRole) || !userRole;
  const isEmployee = userRole === 'employee';

  // ── Persistence ──────────────────────────────────────────────────────────────
  const loadData = useCallback(() => {
    try {
      const emps = JSON.parse(localStorage.getItem('gm_employees') || '[]');
      setEmployees(emps);
      const runs = JSON.parse(localStorage.getItem('gm_payroll_runs') || '[]');
      setPayrollRuns(runs);
      const lock = JSON.parse(localStorage.getItem(`gm_payroll_lock_${selectedMonth}`) || 'false');
      setLocked(lock);
      const pd = JSON.parse(localStorage.getItem(`gm_period_${selectedMonth}`) || '{}');
      setPeriodData(pd);
    } catch {}
  }, [selectedMonth]);

  useEffect(() => { loadData(); }, [loadData]);

  const saveEmployees = (emps) => {
    setEmployees(emps);
    localStorage.setItem('gm_employees', JSON.stringify(emps));
  };

  const saveEmployee = () => {
    if (!empForm.name || !empForm.basic_salary) { notify('Name and basic salary required', 'error'); return; }
    const updated = editEmp
      ? employees.map(e => e.id === editEmp.id ? { ...e, ...empForm } : e)
      : [...employees, { ...empForm, id: Date.now(), created_at: new Date().toISOString() }];
    saveEmployees(updated);
    setShowEmpForm(false);
    setEditEmp(null);
    setEmpForm(blankEmp);
    notify(editEmp ? 'Employee updated' : 'Employee added');
  };

  const deleteEmployee = (id) => {
    saveEmployees(employees.filter(e => e.id !== id));
    notify('Employee removed');
  };

  const updatePeriod = (empId, field, val) => {
    const updated = { ...periodData, [empId]: { ...(periodData[empId] || {}), [field]: val } };
    setPeriodData(updated);
    localStorage.setItem(`gm_period_${selectedMonth}`, JSON.stringify(updated));
  };

  const runPayroll = () => {
    if (locked) { notify('Payroll is locked for this period', 'error'); return; }
    if (employees.length === 0) { notify('No employees to process', 'error'); return; }
    setProcessing(true);
    setTimeout(() => {
      const run = {
        id: Date.now(),
        period: selectedMonth,
        processed_at: new Date().toISOString(),
        employees: employees.filter(e => e.status === 'active').map(emp => {
          const pd = periodData[emp.id] || {};
          const calc = computePayroll(emp, pd);
          return { ...emp, ...pd, ...calc };
        }),
      };
      run.total_gross = run.employees.reduce((s, e) => s + e.grossPay, 0);
      run.total_net = run.employees.reduce((s, e) => s + e.netPay, 0);
      run.total_paye = run.employees.reduce((s, e) => s + e.paye, 0);
      run.total_nssf = run.employees.reduce((s, e) => s + e.nssf, 0);
      run.total_nhif = run.employees.reduce((s, e) => s + e.nhif, 0);
      run.total_housing = run.employees.reduce((s, e) => s + e.housingLevy, 0);

      const updatedRuns = [...payrollRuns.filter(r => r.period !== selectedMonth), run];
      setPayrollRuns(updatedRuns);
      localStorage.setItem('gm_payroll_runs', JSON.stringify(updatedRuns));
      setProcessing(false);
      notify(`Payroll processed for ${selectedMonth} — ${run.employees.length} employees`);
    }, 1000);
  };

  const lockPayroll = () => {
    setLocked(true);
    localStorage.setItem(`gm_payroll_lock_${selectedMonth}`, 'true');
    notify('Payroll locked for ' + selectedMonth);
  };

  const activeEmps = employees.filter(e => e.status === 'active');
  const currentRun = payrollRuns.find(r => r.period === selectedMonth);
  const totalGross = activeEmps.reduce((s, emp) => {
    const pd = periodData[emp.id] || {};
    const calc = computePayroll(emp, pd);
    return s + calc.grossPay;
  }, 0);

  const exportBankCSV = () => {
    if (!currentRun) { notify('Run payroll first', 'error'); return; }
    const rows = [['Employee Name', 'Bank', 'Account', 'Branch', 'Net Pay (KES)']];
    currentRun.employees.forEach(e => {
      rows.push([e.name, e.bank_name || '—', e.bank_account || '—', e.bank_branch || '—', e.netPay.toFixed(2)]);
    });
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${getBrandingSlug()}_Payroll_${selectedMonth}_bank_transfer.csv`;
    a.click();
    notify('Bank transfer CSV exported');
  };



  // ─── Tabs by role ─────────────────────────────────────────────────────────
  const allTabs = [
    { id: 'dashboard',  label: 'Dashboard',       icon: '📊' },
    canHR && { id: 'employees',  label: 'Employees',       icon: '👥' },
    (canHR || canFinance) && { id: 'process',    label: 'Process Payroll', icon: '⚙️' },
    canFinance && { id: 'reports',    label: 'Reports',         icon: '📋' },
    canFinance && { id: 'statutory',  label: 'Statutory',       icon: '🏛️' },
    { id: 'payslips',   label: 'Payslips',        icon: '📄' },
  ].filter(Boolean);

  return (
    <div>
      <PageHeader icon="💼" title="Payroll Management" sub="Kenya statutory compliance — PAYE, NSSF, NHIF, Housing Levy" />

      <StatCards stats={[
        { label:'Active Employees',  value:activeEmps.length,          color:'#3498db',  icon:'👤', sub:`${employees.length} total staff` },
        { label:'Est. Gross Payroll',value:fmtKES(totalGross),          color:'#c8a96e',  icon:'💰', sub:selectedMonth },
        { label:'Est. Net Payroll',  value:fmtKES(Math.round(totalGross*0.72)), color:'#2ecc71', icon:'✅', sub:'After all deductions' },
        { label: locked ? 'Period Locked' : 'Period Open', value: locked ? '🔒' : '🔓', color: locked ? '#e74c3c' : '#2ecc71', icon: locked ? '🔒' : '🔓', sub: selectedMonth },
      ]} />

      {/* Period selector */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ color:'var(--textMuted)', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Payroll Period:</label>
          <input type="month" style={{ ...inputStyle, width: 'auto', padding: '7px 12px' }} value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} />
        </div>
        {currentRun && <span style={{ color: '#2ecc71', fontSize: 12, padding: '4px 10px', background:'var(--bgSuccess)', borderRadius: 3 }}>✓ Processed {currentRun.processed_at?.slice(0, 10)}</span>}
        {locked && <span style={{ color: '#e74c3c', fontSize: 12, padding: '4px 10px', background: '#2a0a0a', borderRadius: 3 }}>🔒 Locked</span>}
      </div>

      <TabBar tabs={allTabs} active={tab} onChange={setTab} />

      {/* ── DASHBOARD ── */}
      {tab === 'dashboard' && (
        <div style={{ display:'grid', gap:14 }}>

          {/* Deduction breakdown */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 240px', gap:12 }}>
            <DashCard title="Payroll Summary">
              {activeEmps.length === 0 ? <Empty text="No active employees" /> : (
                <>
                  {[
                    ['Total Gross',   fmtKES(activeEmps.reduce((s,e)=>s+(parseFloat(e.basic_salary)||0),0)), '#c8a96e'],
                    ['Est. PAYE',     fmtKES(activeEmps.reduce((s,e)=>{ const cc=computePayroll(e,periodData[e.id]||{}); return s+cc.paye; },0)), '#e74c3c'],
                    ['Est. NSSF',     fmtKES(activeEmps.reduce((s,e)=>s+computeNSSF(parseFloat(e.basic_salary)||0),0)), '#3498db'],
                    ['Est. NHIF/SHA', fmtKES(activeEmps.reduce((s,e)=>s+computeNHIF(parseFloat(e.basic_salary)||0),0)), '#9b59b6'],
                    ['Housing Levy',  fmtKES(activeEmps.reduce((s,e)=>s+Math.round((parseFloat(e.basic_salary)||0)*HOUSING_LEVY_RATE),0)), '#e67e22'],
                    ['Est. Net',      fmtKES(Math.round(activeEmps.reduce((s,e)=>s+(parseFloat(e.basic_salary)||0),0)*0.72)), '#2ecc71'],
                  ].map(([l,v,col])=>(
                    <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'9px 0', borderBottom:'1px solid var(--border)' }}>
                      <span style={{ fontSize:12, color:'var(--textMuted)' }}>{l}</span>
                      <span style={{ fontSize:13, fontWeight:700, color:col }}>{v}</span>
                    </div>
                  ))}
                </>
              )}
            </DashCard>

            <DashCard title="Department Payroll">
              <RankBars
                items={DEPTS.filter(d=>employees.some(e=>e.department===d&&e.status==='active')).map(dept=>{
                  const dEmps = activeEmps.filter(e=>e.department===dept);
                  return { label:`${dept} (${dEmps.length})`, value:dEmps.reduce((s,e)=>s+(parseFloat(e.basic_salary)||0),0), color:'#c8a96e' };
                })}
                formatVal={v=>'K'+Math.round(v/1000)}
              />
              {employees.length === 0 && <Empty text="No employees yet" />}
            </DashCard>

            <DashCard title="Workforce">
              <DonutRing
                size={110} stroke={12}
                centerLabel={activeEmps.length}
                centerSub="active"
                segments={[
                  { label:'Active',     value:activeEmps.length,              color:'#2ecc71' },
                  { label:'Inactive',   value:employees.filter(e=>e.status!=='active').length, color:'var(--border)' },
                ].filter(s=>s.value>0)}
              />
              <div style={{ marginTop:12, textAlign:'center' }}>
                <div style={{ fontSize:11, color:'var(--textFaint)' }}>{employees.length} total staff</div>
                <div style={{ fontSize:11, color: locked ? '#e74c3c':'#2ecc71', fontWeight:700, marginTop:4 }}>
                  {locked ? '🔒 Period locked' : '🔓 Period open'}
                </div>
              </div>
            </DashCard>
          </div>
        </div>
      )}

      {/* ── EMPLOYEES ── */}
      {tab === 'employees' && canHR && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button style={btnPrimary} onClick={() => { setShowEmpForm(true); setEditEmp(null); setEmpForm(blankEmp); }}>+ Add Employee</button>
          </div>

          {showEmpForm && (
            <Card title={editEmp ? 'Edit Employee' : 'New Employee'}>
              <div style={{ fontSize: 10, color: '#c8a96e', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12, borderBottom: '1px solid #2a2a2a', paddingBottom: 6 }}>Personal Information</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 10, marginBottom: 16 }}>
                <Field label="Full Name *"><input style={inputStyle} value={empForm.name} onChange={e => setEmpForm({ ...empForm, name: e.target.value })} /></Field>
                <Field label="Employee ID"><input style={inputStyle} value={empForm.emp_id} onChange={e => setEmpForm({ ...empForm, emp_id: e.target.value })} placeholder="EMP-001" /></Field>
                <Field label="Department">
                  <select style={inputStyle} value={empForm.department} onChange={e => setEmpForm({ ...empForm, department: e.target.value })}>
                    <option value="">— Select —</option>
                    {DEPTS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </Field>
                <Field label="Job Title"><input style={inputStyle} value={empForm.job_title} onChange={e => setEmpForm({ ...empForm, job_title: e.target.value })} /></Field>
                <Field label="ID Number"><input style={inputStyle} value={empForm.id_number} onChange={e => setEmpForm({ ...empForm, id_number: e.target.value })} /></Field>
                <Field label="KRA PIN"><input style={inputStyle} value={empForm.kra_pin} onChange={e => setEmpForm({ ...empForm, kra_pin: e.target.value })} placeholder="A123456789Z" /></Field>
                <Field label="NSSF Number"><input style={inputStyle} value={empForm.nssf_number} onChange={e => setEmpForm({ ...empForm, nssf_number: e.target.value })} /></Field>
                <Field label="NHIF / SHA Number"><input style={inputStyle} value={empForm.nhif_number} onChange={e => setEmpForm({ ...empForm, nhif_number: e.target.value })} /></Field>
                <Field label="Phone"><input style={inputStyle} value={empForm.phone} onChange={e => setEmpForm({ ...empForm, phone: e.target.value })} /></Field>
                <Field label="Email"><input style={inputStyle} value={empForm.email} onChange={e => setEmpForm({ ...empForm, email: e.target.value })} /></Field>
              </div>

              <div style={{ fontSize: 10, color: '#c8a96e', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12, borderBottom: '1px solid #2a2a2a', paddingBottom: 6 }}>Salary Structure</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 10, marginBottom: 16 }}>
                <Field label="Basic Salary (KES) *"><input style={inputStyle} type="number" value={empForm.basic_salary} onChange={e => setEmpForm({ ...empForm, basic_salary: e.target.value })} /></Field>
                <Field label="House Allowance"><input style={inputStyle} type="number" value={empForm.house_allowance} onChange={e => setEmpForm({ ...empForm, house_allowance: e.target.value })} placeholder="0" /></Field>
                <Field label="Transport Allowance"><input style={inputStyle} type="number" value={empForm.transport_allowance} onChange={e => setEmpForm({ ...empForm, transport_allowance: e.target.value })} placeholder="0" /></Field>
                <Field label="Medical Allowance"><input style={inputStyle} type="number" value={empForm.medical_allowance} onChange={e => setEmpForm({ ...empForm, medical_allowance: e.target.value })} placeholder="0" /></Field>
                <Field label="Other Allowances"><input style={inputStyle} type="number" value={empForm.other_allowance} onChange={e => setEmpForm({ ...empForm, other_allowance: e.target.value })} placeholder="0" /></Field>
                <Field label="Employment Type">
                  <select style={inputStyle} value={empForm.employment_type} onChange={e => setEmpForm({ ...empForm, employment_type: e.target.value })}>
                    <option value="permanent">Permanent</option>
                    <option value="contract">Contract</option>
                    <option value="casual">Casual</option>
                    <option value="intern">Intern</option>
                  </select>
                </Field>
                <Field label="Start Date"><input style={inputStyle} type="date" value={empForm.start_date} onChange={e => setEmpForm({ ...empForm, start_date: e.target.value })} /></Field>
                <Field label="Status">
                  <select style={inputStyle} value={empForm.status} onChange={e => setEmpForm({ ...empForm, status: e.target.value })}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </Field>
                <Field label="Loan Balance (KES)"><input style={inputStyle} type="number" value={empForm.loan_balance} onChange={e => setEmpForm({ ...empForm, loan_balance: e.target.value })} placeholder="0" /></Field>
                <Field label="Advance Balance (KES)"><input style={inputStyle} type="number" value={empForm.advance_balance} onChange={e => setEmpForm({ ...empForm, advance_balance: e.target.value })} placeholder="0" /></Field>
              </div>

              <div style={{ fontSize: 10, color: '#c8a96e', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12, borderBottom: '1px solid #2a2a2a', paddingBottom: 6 }}>Bank Information</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 10, marginBottom: 16 }}>
                <Field label="Bank Name"><input style={inputStyle} value={empForm.bank_name} onChange={e => setEmpForm({ ...empForm, bank_name: e.target.value })} placeholder="KCB, Equity, Co-op…" /></Field>
                <Field label="Account Number"><input style={inputStyle} value={empForm.bank_account} onChange={e => setEmpForm({ ...empForm, bank_account: e.target.value })} /></Field>
                <Field label="Branch"><input style={inputStyle} value={empForm.bank_branch} onChange={e => setEmpForm({ ...empForm, bank_branch: e.target.value })} /></Field>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button style={btnPrimary} onClick={saveEmployee}>✓ Save Employee</button>
                <button style={{ ...btnSm, padding: '10px 18px' }} onClick={() => { setShowEmpForm(false); setEditEmp(null); }}>Cancel</button>
              </div>
            </Card>
          )}

          <Card title={`Employees (${employees.length})`}>
            {employees.length === 0 ? <Empty text="No employees yet. Add your first employee above." /> : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead><tr style={{ borderBottom: '1px solid #2a2a2a' }}>
                    {['Name / ID', 'Department', 'Title', 'Basic', 'Type', 'Status', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '8px 10px', color: '#c8a96e', textAlign: 'left', fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {employees.map(emp => (
                      <tr key={emp.id} style={{ borderBottom: '1px solid #1a1a1a' }}>
                        <td style={{ padding: '9px 10px' }}>
                          <div style={{ color:'var(--textPrimary)', fontWeight: 600 }}>{emp.name}</div>
                          <div style={{ color:'var(--textFaint)', fontSize: 11 }}>{emp.emp_id || '—'} · {emp.id_number || '—'}</div>
                        </td>
                        <td style={{ padding: '9px 10px', color:'var(--textMuted)', fontSize: 12 }}>{emp.department}</td>
                        <td style={{ padding: '9px 10px', color:'var(--textMuted)', fontSize: 12 }}>{emp.job_title}</td>
                        <td style={{ padding: '9px 10px', color: '#c8a96e', fontWeight: 700 }}>{fmtKES(emp.basic_salary)}</td>
                        <td style={{ padding: '9px 10px', color:'var(--textFaint)', fontSize: 11, textTransform: 'capitalize' }}>{emp.employment_type}</td>
                        <td style={{ padding: '9px 10px' }}>
                          <span style={{ padding: '2px 7px', borderRadius: 3, fontSize: 10, color: emp.status === 'active' ? '#2ecc71' : '#e74c3c', background: emp.status === 'active' ? '#1a3a1a' : '#2a0a0a', textTransform: 'uppercase' }}>{emp.status}</span>
                        </td>
                        <td style={{ padding: '9px 10px' }}>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button style={{ ...btnSm, fontSize: 10 }} onClick={() => { setEditEmp(emp); setEmpForm(emp); setShowEmpForm(true); }}>Edit</button>
                            <button style={{ ...btnSm, fontSize: 10 }} onClick={() => setSelectedEmp(emp)}>Payslip</button>
                            <button style={{ ...btnSm, fontSize: 10, color: '#e74c3c' }} onClick={() => deleteEmployee(emp.id)}>Del</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {/* ── PROCESS PAYROLL ── */}
      {tab === 'process' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            {!locked && <button style={btnPrimary} onClick={runPayroll} disabled={processing || activeEmps.length === 0}>{processing ? '⏳ Processing…' : '▶ Run Payroll'}</button>}
            {currentRun && !locked && <button style={{ ...btnSm, background: '#2a0a0a', color: '#e74c3c', padding: '10px 18px' }} onClick={lockPayroll}>🔒 Lock Payroll</button>}
            {currentRun && <button style={{ ...btnSm, background: '#0a1a2a', color: '#3498db', padding: '10px 18px' }} onClick={exportBankCSV}>📊 Export Bank CSV</button>}
          </div>

          {locked && <div style={{ padding: '10px 14px', background: '#2a0a0a', border: '1px solid #e74c3c44', borderRadius: 4, color: '#e74c3c', fontSize: 13, marginBottom: 14 }}>🔒 This payroll period is locked. No further changes can be made.</div>}

          <Card title={`Payroll Preview — ${selectedMonth}`}>
            {activeEmps.length === 0 ? <Empty text="No active employees" /> : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead><tr style={{ borderBottom: '1px solid #2a2a2a' }}>
                    {['Employee', 'Basic', 'Allowances', 'OT / Bonus', 'Gross', 'PAYE', 'NSSF', 'NHIF', 'H.Levy', 'Other', 'Net Pay', ''].map(h => (
                      <th key={h} style={{ padding: '7px 8px', color: '#c8a96e', textAlign: 'right', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {activeEmps.map(emp => {
                      const pd = periodData[emp.id] || {};
                      const calc = computePayroll(emp, pd);
                      const allows = calc.house + calc.transport + calc.medical + calc.otherAllow;
                      return (
                        <tr key={emp.id} style={{ borderBottom: '1px solid #1a1a1a' }}>
                          <td style={{ padding: '8px 8px', textAlign: 'left' }}>
                            <div style={{ color:'var(--textPrimary)', fontWeight: 600, whiteSpace: 'nowrap' }}>{emp.name}</div>
                            <div style={{ color:'var(--textFaint)', fontSize: 10 }}>{emp.department}</div>
                          </td>
                          <td style={{ padding: '8px 8px', color:'var(--textMuted)', textAlign: 'right' }}>{fmtKES(calc.basic)}</td>
                          <td style={{ padding: '8px 8px', color:'var(--textMuted)', textAlign: 'right' }}>{fmtKES(allows)}</td>
                          <td style={{ padding: '8px 8px', textAlign: 'right' }}>
                            {!locked ? (
                              <div style={{ display: 'flex', gap: 4, flexDirection: 'column' }}>
                                <input type="number" placeholder="OT" style={{ ...inputStyle, padding: '3px 5px', fontSize: 11, width: 60, textAlign: 'right' }} value={pd.overtime_pay || ''} onChange={e => updatePeriod(emp.id, 'overtime_pay', e.target.value)} />
                                <input type="number" placeholder="Bonus" style={{ ...inputStyle, padding: '3px 5px', fontSize: 11, width: 60, textAlign: 'right' }} value={pd.bonus || ''} onChange={e => updatePeriod(emp.id, 'bonus', e.target.value)} />
                              </div>
                            ) : <span style={{ color:'var(--textMuted)' }}>{fmtKES(calc.overtime + calc.bonus)}</span>}
                          </td>
                          <td style={{ padding: '8px 8px', color: '#c8a96e', fontWeight: 700, textAlign: 'right' }}>{fmtKES(calc.grossPay)}</td>
                          <td style={{ padding: '8px 8px', color: '#e74c3c', textAlign: 'right' }}>{fmtKES(calc.paye)}</td>
                          <td style={{ padding: '8px 8px', color: '#e67e22', textAlign: 'right' }}>{fmtKES(calc.nssf)}</td>
                          <td style={{ padding: '8px 8px', color: '#9b59b6', textAlign: 'right' }}>{fmtKES(calc.nhif)}</td>
                          <td style={{ padding: '8px 8px', color: '#3498db', textAlign: 'right' }}>{fmtKES(calc.housingLevy)}</td>
                          <td style={{ padding: '8px 8px', textAlign: 'right' }}>
                            {!locked ? (
                              <div style={{ display: 'flex', gap: 4, flexDirection: 'column' }}>
                                <input type="number" placeholder="Loan" style={{ ...inputStyle, padding: '3px 5px', fontSize: 11, width: 60, textAlign: 'right' }} value={pd.loan_deduction || ''} onChange={e => updatePeriod(emp.id, 'loan_deduction', e.target.value)} />
                              </div>
                            ) : <span style={{ color:'var(--textMuted)' }}>{fmtKES(calc.loanDeduct)}</span>}
                          </td>
                          <td style={{ padding: '8px 8px', color: '#2ecc71', fontWeight: 700, textAlign: 'right', fontSize: 14 }}>{fmtKES(calc.netPay)}</td>
                          <td style={{ padding: '8px 8px' }}>
                            <button style={{ ...btnSm, fontSize: 10 }} onClick={() => printPayslip(emp, calc, selectedMonth)}>🖨</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid #c8a96e44', background: '#1a1204' }}>
                      <td colSpan="4" style={{ padding: '10px 8px', color: '#c8a96e', fontWeight: 700, textAlign: 'left' }}>TOTALS ({activeEmps.length} employees)</td>
                      <td style={{ padding: '10px 8px', color: '#c8a96e', fontWeight: 700, textAlign: 'right' }}>{fmtKES(activeEmps.reduce((s, e) => { const c = computePayroll(e, periodData[e.id] || {}); return s + c.grossPay; }, 0))}</td>
                      <td style={{ padding: '10px 8px', color: '#e74c3c', fontWeight: 700, textAlign: 'right' }}>{fmtKES(activeEmps.reduce((s, e) => { const c = computePayroll(e, periodData[e.id] || {}); return s + c.paye; }, 0))}</td>
                      <td style={{ padding: '10px 8px', color: '#e67e22', fontWeight: 700, textAlign: 'right' }}>{fmtKES(activeEmps.reduce((s, e) => s + computeNSSF(parseFloat(e.basic_salary) || 0), 0))}</td>
                      <td style={{ padding: '10px 8px', color: '#9b59b6', fontWeight: 700, textAlign: 'right' }}>{fmtKES(activeEmps.reduce((s, e) => s + computeNHIF(parseFloat(e.basic_salary) || 0), 0))}</td>
                      <td style={{ padding: '10px 8px', color: '#3498db', fontWeight: 700, textAlign: 'right' }}>{fmtKES(activeEmps.reduce((s, e) => s + Math.round((parseFloat(e.basic_salary) || 0) * HOUSING_LEVY_RATE), 0))}</td>
                      <td colSpan="2" />
                      <td style={{ padding: '10px 8px', color: '#2ecc71', fontWeight: 700, textAlign: 'right', fontSize: 15 }}>{fmtKES(activeEmps.reduce((s, e) => { const c = computePayroll(e, periodData[e.id] || {}); return s + c.netPay; }, 0))}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {/* ── STATUTORY REPORTS ── */}
      {tab === 'statutory' && canFinance && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 16 }}>
          {[
            { title: 'PAYE Summary (KRA)', color: '#e74c3c', key: 'paye', desc: 'File monthly by 9th via iTax' },
            { title: 'NSSF Returns', color: '#e67e22', key: 'nssf', desc: 'File by 15th of following month' },
            { title: 'NHIF / SHA Returns', color: '#9b59b6', key: 'nhif', desc: 'File by 9th of following month' },
            { title: 'Housing Levy (KRBF)', color: '#3498db', key: 'housingLevy', desc: 'File monthly by 9th' },
          ].map(({ title, color, key, desc }) => {
            const total = activeEmps.reduce((s, e) => {
              const c = computePayroll(e, periodData[e.id] || {});
              return s + (c[key] || 0);
            }, 0);
            return (
              <Card key={key} title={title}>
                <div style={{ fontSize: 32, fontWeight: 700, color, marginBottom: 8 }}>{fmtKES(total)}</div>
                <div style={{ color:'var(--textMuted)', fontSize: 12, marginBottom: 12 }}>{desc}</div>
                <div style={{ overflowX: 'auto', maxHeight: 200, overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead><tr><th style={{ padding: '5px 8px', color:'var(--textMuted)', textAlign: 'left', fontSize: 10 }}>Employee</th><th style={{ padding: '5px 8px', color:'var(--textMuted)', textAlign: 'right', fontSize: 10 }}>Amount</th></tr></thead>
                    <tbody>
                      {activeEmps.map(emp => {
                        const c = computePayroll(emp, periodData[emp.id] || {});
                        return (
                          <tr key={emp.id} style={{ borderBottom: '1px solid #1a1a1a' }}>
                            <td style={{ padding: '5px 8px', color:'var(--textMuted)' }}>{emp.name}</td>
                            <td style={{ padding: '5px 8px', color, fontWeight: 600, textAlign: 'right' }}>{fmtKES(c[key] || 0)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* ── PAYSLIPS ── */}
      {tab === 'payslips' && (
        <Card title="Generate Payslips">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 12 }}>
            {activeEmps.map(emp => {
              const pd = periodData[emp.id] || {};
              const calc = computePayroll(emp, pd);
              return (
                <div key={emp.id} style={{ background:'var(--bgCard)', border: '1px solid #2a2a2a', borderRadius: 4, padding: 16 }}>
                  <div style={{ fontWeight: 700, color:'var(--textPrimary)', fontSize: 14, marginBottom: 4 }}>{emp.name}</div>
                  <div style={{ color:'var(--textMuted)', fontSize: 12, marginBottom: 8 }}>{emp.job_title} · {emp.department}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 6, marginBottom: 10 }}>
                    <div style={{ background: 'var(--bgInput)', borderRadius: 3, padding: '6px 8px' }}>
                      <div style={{ fontSize: 9, color:'var(--textFaint)', textTransform: 'uppercase' }}>Gross</div>
                      <div style={{ color: '#c8a96e', fontWeight: 700 }}>{fmtKES(calc.grossPay)}</div>
                    </div>
                    <div style={{ background: 'var(--bgInput)', borderRadius: 3, padding: '6px 8px' }}>
                      <div style={{ fontSize: 9, color:'var(--textFaint)', textTransform: 'uppercase' }}>Net Pay</div>
                      <div style={{ color: '#2ecc71', fontWeight: 700 }}>{fmtKES(calc.netPay)}</div>
                    </div>
                  </div>
                  <button style={{ ...btnSm, width: '100%', textAlign: 'center' }} onClick={() => printPayslip(emp, calc)}>🖨️ Print Payslip</button>
                </div>
              );
            })}
            {activeEmps.length === 0 && <Empty text="No active employees" />}
          </div>
        </Card>
      )}

      {/* ── REPORTS ── */}
      {tab === 'reports' && canFinance && (
        <div style={{ display: 'grid', gap: 16 }}>
          <Card title="YTD Payroll Summary" action={<button style={{ ...btnSm, color: '#3498db' }} onClick={exportBankCSV}>📊 Bank CSV</button>}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr style={{ borderBottom: '1px solid #2a2a2a' }}>
                  {['Period', 'Employees', 'Gross Payroll', 'PAYE', 'NSSF', 'NHIF', 'Net Payroll', 'Status'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', color: '#c8a96e', textAlign: 'left', fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {payrollRuns.map(run => (
                    <tr key={run.id} style={{ borderBottom: '1px solid #1a1a1a' }}>
                      <td style={{ padding: '8px 10px', color: '#c8a96e', fontWeight: 600 }}>{run.period}</td>
                      <td style={{ padding: '8px 10px', color:'var(--textMuted)' }}>{run.employees?.length || 0}</td>
                      <td style={{ padding: '8px 10px', color:'var(--textPrimary)', fontWeight: 600 }}>{fmtKES(run.total_gross)}</td>
                      <td style={{ padding: '8px 10px', color: '#e74c3c' }}>{fmtKES(run.total_paye)}</td>
                      <td style={{ padding: '8px 10px', color: '#e67e22' }}>{fmtKES(run.total_nssf)}</td>
                      <td style={{ padding: '8px 10px', color: '#9b59b6' }}>{fmtKES(run.total_nhif)}</td>
                      <td style={{ padding: '8px 10px', color: '#2ecc71', fontWeight: 700 }}>{fmtKES(run.total_net)}</td>
                      <td style={{ padding: '8px 10px' }}>
                        <span style={{ padding: '2px 7px', borderRadius: 3, fontSize: 10, color: '#2ecc71', background:'var(--bgSuccess)' }}>Processed</span>
                      </td>
                    </tr>
                  ))}
                  {payrollRuns.length === 0 && <tr><td colSpan="8" style={{ padding: 20, textAlign: 'center', color:'var(--textFaint)' }}>No payroll runs yet</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
